import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import styles from '../../styles/modules/OfflinePayment.module.css'
import { useSelector } from 'react-redux'
import { PaymentAPI } from '../../api/PaymentAPI'
import {
    FormControl, FormControlLabel, FormLabel,
    Radio, RadioGroup, Tooltip, Button,
} from '@mui/material'
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid'
import { FaFileExport } from 'react-icons/fa'
import { isUser } from '../../util/UserRoles'
import { Link } from 'react-router-dom'
import { BsCloudDownload } from 'react-icons/bs'

// ✅ Date format - only show date, no time
const dateFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
}

// ✅ Custom Toolbar with built-in quick filter
const CustomToolbar = ({ onExportClick, buttonRef, quickFilterProps }) => {
    return (
        <div style={{
            width: '100%',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            padding: '8px 16px',
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px',
            }}>
                <div style={{ flex: 1 }}>
                    <GridToolbarQuickFilter
                        {...quickFilterProps}
                        sx={{
                            '& .MuiInputBase-root': {
                                borderRadius: '8px',
                                height: '38px',
                                backgroundColor: 'white',
                                width: '320px',
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '14px',
                                padding: '0 12px',
                            },
                        }}
                        placeholder="Search by Transaction ID or Player..."
                        debounceMs={400}
                        quickFilterParser={(searchInput) =>
                            searchInput.split(',').map((value) => value.trim())
                        }
                    />
                </div>
                <div>
                    <Tooltip title="Export Report">
                        <Button
                            ref={buttonRef}
                            variant="contained"
                            size="small"
                            onClick={onExportClick}
                            startIcon={<FaFileExport />}
                            sx={{
                                backgroundColor: 'var(--hoverColorBold)',
                                '&:hover': {
                                    backgroundColor: 'var(--hoverColorBold)',
                                    opacity: 0.9,
                                },
                                textTransform: 'none',
                                fontWeight: 500,
                                borderRadius: '8px',
                                padding: '4px 16px',
                                fontSize: '13px',
                                pointerEvents: 'auto',
                            }}
                        >
                            Export Report
                        </Button>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

const OnlinePayment = () => {
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const userInfo = useSelector((store) => store.user.userInfo);
    const [paymentHistories, setPaymentHistories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [totalPaymentHistory, setTotalPaymentHistory] = useState(0);
    const [dateRange, setDateRange] = useState('currentYear');
    const [filterData] = useState({});
    const [filterModel, setFilterModel] = useState({ items: [] });

    // ✅ Track sorting state with default sort by createdDate DESC
    const [sortModel, setSortModel] = useState([
        { field: 'createdDate', sort: 'desc' }
    ]);

    const exportButtonRef = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // ✅ Handle API call with filter and sorting
    const handleFetchPaymentHistory = async (filterText = '', size, pageNum, sortData = null) => {
        try {
            const finalSize = size !== undefined ? size : paginationModel.pageSize;
            const finalPage = pageNum !== undefined ? pageNum : paginationModel.page;

            let sortParams = {};
            if (sortData) {
                sortParams = sortData;
            } else if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'createdDate': 'createdDate',
                    'playerName': 'playerName',
                    'status': 'status',
                    'paymentFor': 'paymentFor',
                    'paymentMethodName': 'paymentMethodName',
                    'amount': 'amount'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }

            console.log("🔍 Calling API with:", { 
                page: finalPage, 
                size: finalSize, 
                filter: filterText,
                sort: sortParams 
            });

            setIsLoading(true);

            let response = null;
            if (isUser(userInfo)) {
                response = await PaymentAPI.getAllPaymentHistoryByUser(
                    selectedClub.clubId,
                    userInfo.userId,
                    finalSize,
                    finalPage,
                    "ONLINE",
                    filterText || '',
                    filterData,
                    sortParams
                );
            } else {
                response = await PaymentAPI.getAllPaymentHistory(
                    selectedClub.clubId,
                    finalSize,
                    finalPage,
                    "ONLINE",
                    filterText || '',
                    filterData,
                    sortParams
                );
            }

            const dataArray = response?.Data || [];
            setPaymentHistories(dataArray);
            setTotalPaymentHistory(
                dataArray.length > 0 ? dataArray[0]?.totalPaymentHistory || 0 : 0
            );
        } catch (error) {
            console.error('❌ Error fetching payment history:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Handle sort model changes
    const handleSortModelChange = useCallback((newSortModel) => {
        setSortModel(newSortModel);
        const filterValue = filterModel?.quickFilterValues?.join(',') || '';
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        let sortParams = {};
        if (newSortModel && newSortModel.length > 0) {
            const sort = newSortModel[0];
            const fieldMap = {
                'createdDate': 'createdDate',
                'playerName': 'playerName',
                'status': 'status',
                'paymentFor': 'paymentFor',
                'paymentMethodName': 'paymentMethodName',
                'amount': 'amount'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        handleFetchPaymentHistory(filterValue, paginationModel.pageSize, 0, sortParams);
    }, [paginationModel.pageSize, filterModel]);

    // ✅ Handle filter changes from DataGrid
    const handleFilterChange = useCallback((newFilterModel) => {
        setFilterModel(newFilterModel);
        const quickFilterValue = newFilterModel?.quickFilterValues?.join(',') || '';
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        let sortParams = {};
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            const fieldMap = {
                'createdDate': 'createdDate',
                'playerName': 'playerName',
                'status': 'status',
                'paymentFor': 'paymentFor',
                'paymentMethodName': 'paymentMethodName',
                'amount': 'amount'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        handleFetchPaymentHistory(quickFilterValue, paginationModel.pageSize, 0, sortParams);
    }, [paginationModel.pageSize, sortModel]);

    const downloadReceiptUrl = async (paymentIntentId) => {
        try {
            const blob = await PaymentAPI.fetchReceiptUrl(
                userInfo.userId,
                selectedClub.clubId,
                paymentIntentId
            );

            if (blob.size === 0) {
                throw new Error('Received empty PDF');
            }

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `receipt-${paymentIntentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => {
                window.URL.revokeObjectURL(downloadUrl);
            }, 100);

        } catch (error) {
            console.error("Error downloading receipt:", error);
            alert(`Failed to download receipt: ${error.message}`);
        }
    };

    const downloadPaymentHistoryReport = async () => {
        try {
            setIsLoading(true);
            const response = await PaymentAPI.downloadPaymentHistory(selectedClub.clubId, dateRange, "ONLINE");
            const url = window.URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Online Payment History Report.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
            setDropdownOpen(false);
        }
    };

    const radioBtnStyle = {
        '&.Mui-checked': {
            color: 'var(--hoverColorBold)',
        },
        '&.Mui-checked ~ .MuiFormControlLabel-label': {
            color: 'var(--hoverColorBold)',
        },
    };

    const handleDropdownToggle = () => {
        if (!dropdownOpen && exportButtonRef.current) {
            const rect = exportButtonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 8,
                left: rect.left - 100,
            });
        }
        setDropdownOpen(!dropdownOpen);
    };

    const handleDropdownClose = () => {
        setDropdownOpen(false);
    };

    // ✅ Initial load and pagination changes
    useEffect(() => {
        if (selectedClub?.clubId) {
            const filterValue = filterModel?.quickFilterValues?.join(',') || '';
            
            let sortParams = {};
            if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'createdDate': 'createdDate',
                    'playerName': 'playerName',
                    'status': 'status',
                    'paymentFor': 'paymentFor',
                    'paymentMethodName': 'paymentMethodName',
                    'amount': 'amount'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
            handleFetchPaymentHistory(filterValue, paginationModel.pageSize, paginationModel.page, sortParams);
        }
    }, [selectedClub?.clubId, paginationModel.page, paginationModel.pageSize]);

    // ✅ Columns with proper null/type safety
    const gridColumns = useMemo(() => [
        {
            field: 'transactionId',
            headerName: 'Transaction ID',
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => {
                const value = params?.value;
                const displayValue = value != null ? String(value) : '';
                return (
                    <Tooltip title={displayValue} arrow>
                        <span style={{ 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            display: 'block',
                            width: '100%'
                        }}>
                            {displayValue}
                        </span>
                    </Tooltip>
                );
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'createdDate',
            headerName: 'Date',
            flex: 1,
            minWidth: 130,
            valueFormatter: (params) => {
                const dateValue = params?.value;
                if (dateValue == null) return '';
                try {
                    const date = new Date(dateValue);
                    if (isNaN(date.getTime())) return '';
                    return date.toLocaleDateString('en-US', dateFormatOptions);
                } catch {
                    return '';
                }
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'playerName',
            headerName: 'Player',
            flex: 1.2,
            minWidth: 140,
            valueFormatter: (params) => {
                const value = params?.value;
                return value != null ? String(value) : '';
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'paymentFor',
            headerName: 'Pay For',
            flex: 1.2,
            minWidth: 130,
            valueFormatter: (params) => {
                const value = params?.value;
                return value != null ? String(value) : '';
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'paymentMethodName',
            headerName: 'Payment Method',
            flex: 1.2,
            minWidth: 130,
            valueFormatter: (params) => {
                let val = params?.value;
                if (val == null) return '';
                val = String(val);
                return val.includes('-') ? val.split("-")[1] : val;
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => {
                const val = params?.value;
                if (val == null) {
                    return <span>Unknown</span>;
                }
                const status = String(val);
                let statusClass = 'pending';
                if (status === 'succeeded') statusClass = 'succeeded';
                else if (status === 'refunded') statusClass = 'refunded';
                else if (status === 'canceled') statusClass = 'canceled';
                return (
                    <span className={`${styles['table_row_data_status']} ${styles[statusClass]}`}>
                        {status}
                    </span>
                );
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'amount',
            headerName: 'Gross Amount',
            flex: 0.8,
            minWidth: 110,
            valueFormatter: (params) => {
                let value = params?.value;
                if (value == null || value === '') return '$0.00 USD';
                const numValue = parseFloat(value);
                if (isNaN(numValue)) return '$0.00 USD';
                return `$${numValue.toFixed(2)} USD`;
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'processingFee',
            headerName: 'Processing Fee',
            flex: 0.8,
            minWidth: 110,
            valueFormatter: (params) => {
                let value = params?.value;
                if (value == null || value === '') return '$0.00 USD';
                const numValue = parseFloat(value);
                if (isNaN(numValue)) return '$0.00 USD';
                return `$${numValue.toFixed(2)} USD`;
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'netAmount',
            headerName: 'Net Amount',
            flex: 0.8,
            minWidth: 110,
            valueFormatter: (params) => {
                let value = params?.value;
                if (value == null || value === '') return '$0.00 USD';
                const numValue = parseFloat(value);
                if (isNaN(numValue)) return '$0.00 USD';
                return `$${numValue.toFixed(2)} USD`;
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'refundedAmount',
            headerName: 'Refunded Amount',
            flex: 0.8,
            minWidth: 110,
            valueFormatter: (params) => {
                let value = params?.value;
                if (value == null || value === '') return '$0.00 USD';
                const numValue = parseFloat(value);
                if (isNaN(numValue)) return '$0.00 USD';
                return `$${numValue.toFixed(2)} USD`;
            },
            sortable: true,
            filterable: false,
        },
        {
            field: 'stripePaymentId',
            headerName: 'Receipt',
            flex: 0.5,
            minWidth: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const value = params?.value;
                if (!value) return null;
                return (
                    <Link className='pdf_dowload_btn' onClick={() => downloadReceiptUrl(value)}>
                        <BsCloudDownload />
                    </Link>
                );
            }
        }
    ], []);

    // ✅ Filter columns based on user role
    const visibleColumns = useMemo(() => {
        return isUser(userInfo)
            ? gridColumns
            : gridColumns.filter(col => col.field !== 'stripePaymentId');
    }, [userInfo, gridColumns]);

    return (
        <>
            <div className={styles['offline_payment_history_container']}>
                <div style={{ height: 750, width: '100%' }}>
                    <DataGrid
                        rows={paymentHistories}
                        columns={visibleColumns}
                        pagination
                        paginationMode="server"
                        rowCount={totalPaymentHistory}
                        paginationModel={paginationModel}
                        onPaginationModelChange={(newModel) => {
                            setPaginationModel(newModel);
                        }}
                        pageSizeOptions={[50, 100]}
                        loading={isLoading}
                        disableSelectionOnClick
                        getRowId={(row) => row.transactionId || row.id}
                        
                        // ✅ Sorting
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        
                        // ✅ Filter handling
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterChange}
                        
                        // ✅ Ensure column resizing is enabled
                        disableColumnResize={false}
                        
                        slots={{
                            toolbar: () => (
                                <CustomToolbar
                                    buttonRef={exportButtonRef}
                                    onExportClick={handleDropdownToggle}
                                    quickFilterProps={{
                                        debounceMs: 400,
                                    }}
                                />
                            ),
                        }}
                        hideFooterPagination={false}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f5f5f5',
                                borderBottom: '2px solid #e8e8e8',
                                fontWeight: 600,
                                color: '#333',
                                fontSize: '14px',
                                textTransform: 'uppercase',
                            },
                            // ✅ Clean column separator - let MUI handle resizing
                            '& .MuiDataGrid-columnSeparator': {
                                visibility: 'visible',
                                color: '#d1d1d1',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                                padding: '8px 16px',
                                fontSize: '14px',
                                color: '#333',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#f5f9ff',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid #e8e8e8',
                                padding: '8px 16px',
                                backgroundColor: '#fafafa',
                            },
                            border: 'none',
                            borderRadius: '8px',
                            '& .MuiDataGrid-toolbarContainer': {
                                padding: 0,
                            },
                            // ✅ Hide column menu icon
                            '& .MuiDataGrid-menuIcon': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
                                display: 'none !important',
                            },
                            '& .MuiDataGrid-columnHeader:focus': {
                                outline: 'none',
                            },
                        }}
                    />
                </div>
            </div>

            {dropdownOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        zIndex: 9999,
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        padding: '16px',
                        minWidth: '220px',
                        maxWidth: '280px',
                    }}
                >
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: -1,
                        }}
                        onClick={handleDropdownClose}
                    />

                    <h3 style={{
                        margin: '0 0 8px 0',
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#333'
                    }}>
                        Export Online Payments
                    </h3>
                    <hr style={{
                        margin: '8px 0 12px 0',
                        border: 'none',
                        borderTop: '1px solid #e8e8e8'
                    }} />
                    <div style={{ marginBottom: '12px' }}>
                        <FormControl>
                            <FormLabel sx={{
                                '&.Mui-focused': {
                                    color: 'var(--hoverColorBold)',
                                },
                                fontWeight: 600,
                                fontSize: '14px',
                            }} id="demo-radio-buttons-group-label">
                                Date Range
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                name="radio-buttons-group"
                                value={dateRange}
                                onChange={(e) => setDateRange(e.target.value)}
                            >
                                <FormControlLabel
                                    value="currentYear"
                                    control={<Radio sx={radioBtnStyle} />}
                                    label="Current Year"
                                />
                                <FormControlLabel
                                    value="pastYear"
                                    control={<Radio sx={radioBtnStyle} />}
                                    label="Past Year"
                                />
                            </RadioGroup>
                        </FormControl>
                    </div>
                    <Button
                        variant="contained"
                        onClick={downloadPaymentHistoryReport}
                        fullWidth
                        sx={{
                            backgroundColor: 'var(--hoverColorBold)',
                            '&:hover': {
                                backgroundColor: 'var(--hoverColorBold)',
                                opacity: 0.9,
                            },
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            padding: '8px 16px',
                            pointerEvents: 'auto',
                        }}
                    >
                        Export
                    </Button>
                </div>
            )}
        </>
    )
}

export default OnlinePayment